/**
 * Conversation sections of the sidebar with my own organization (feature "channels"): Starred, my folders (channels,
 * DMs and docs items), Channels, Direct messages and Agents. Rows drag within and between sections (dnd-kit sortable
 * groups; pages also drag in from the Docs tree), and every row menu offers the same moves without a pointer ("Move to
 * folder…"). The live drag preview is local; the drop goes to `SidebarViewModel.drop`, which plans fractional ranks.
 */
import { DragDropProvider, useDraggable, useDroppable } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { move } from "@bundle/ui/primitives/sortableMove.ts";
import { Shell, Sidebar as SidebarLogic, type OpenTarget, type SidebarEntry, type SidebarSection } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import {
  EmptyFolderHint,
  SidebarAgentRow,
  SidebarChannelRow,
  SidebarDmRow,
  SidebarFolderPageRow,
  SidebarNavRow,
  SidebarSectionHeader,
  useNewMessageSheet,
  useFeatureAccess,
  useSidebar,
  useT,
  useTr,
  useWorkspace,
  type MenuItem,
} from "@bundle/ui";
import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { ModClick } from "./SidebarDocs.tsx";

const SECTION_ICONS: Record<string, IconName> = { starred: "star", channels: "message", dms: "users", agents: "agent" };
const ROW_TYPES = ["channel", "dm", "page"] as const;

type Items = Record<string, string[]>;
type DragState = { items: Items; source: { id: string; section: string | undefined; type: string } } | undefined;

/** The live drag (shared by the docs tree and the sections inside one provider). */
const DragContext = createContext<{ drag: DragState; dragging: string | undefined }>({ drag: undefined, dragging: undefined });

function itemsOf(sections: SidebarSection[]): Items {
  const out: Items = {};
  for (const s of sections) out[s.id] = s.rows.map((r) => r.id);
  return out;
}

function accepts(sectionId: string): string[] {
  return ROW_TYPES.filter((t) => SidebarLogic.sectionAccepts(sectionId, t));
}

function sectionOf(items: Items, id: string): string | undefined {
  return Object.keys(items).find((k) => items[k]!.includes(id));
}

/**
 * The live preview of dragging `sourceId` over `targetId` (a row, or a section's drop area = its end): within a
 * section the rows swap places like a sortable list; into another section it goes before the hovered row.
 */
function preview(items: Items, sourceId: string, targetId: string): Items {
  if (sourceId === targetId) return items;
  const isSection = items[targetId] !== undefined;
  const to = isSection ? targetId : sectionOf(items, targetId);
  if (to === undefined) return items;
  const from = sectionOf(items, sourceId);
  if (from === to && isSection) return items;
  const next: Items = {};
  for (const k of Object.keys(items)) next[k] = items[k]!.filter((x) => x !== sourceId);
  const list = next[to]!;
  let at = isSection ? list.length : list.indexOf(targetId);
  if (from === to && !isSection && items[to]!.indexOf(sourceId) < items[to]!.indexOf(targetId)) at += 1;
  next[to] = [...list.slice(0, at), sourceId, ...list.slice(at)];
  return next;
}

/**
 * Drag-and-drop provider around the docs tree and the sections, so docs items can be dropped into folders. Renders a
 * virtual Starred section while a conversation is dragged and Starred is empty.
 */
export function SidebarDnd({ children }: { children: ReactNode }) {
  const sidebar = useSidebar();
  const sections = sidebar.state.sections;
  const [drag, setDrag] = useState<DragState>(undefined);
  const latest = useRef<DragState>(undefined);
  const set = (next: DragState) => {
    latest.current = next;
    setDrag(next);
  };
  const base = () => {
    const items = itemsOf(sections);
    if (items.starred === undefined) items.starred = [];
    return items;
  };
  const value = useMemo(() => ({ drag, dragging: drag?.source.id }), [drag]);
  return (
    <DragDropProvider
      onDragStart={(event) => {
        const source = event.operation.source;
        if (source === null) return;
        const id = String(source.id);
        const items = base();
        set({ items, source: { id, section: sectionOf(items, id), type: String(source.type ?? "") } });
      }}
      onDragOver={(event) => {
        const d = latest.current;
        const target = event.operation.target;
        if (d === undefined || target === null) return;
        // Rows are sortables: keep the item state in step with dnd-kit's optimistic sorting (as the Board does).
        if (d.source.section !== undefined) {
          set({ ...d, items: move(d.items, event) });
          return;
        }
        // A docs item dragged in from the page tree is a plain draggable: preview it in the folder it hovers.
        const targetId = String(target.id);
        const toSection = d.items[targetId] !== undefined ? targetId : sectionOf(d.items, targetId);
        if (toSection === undefined || !SidebarLogic.sectionAccepts(toSection, d.source.type)) return;
        const items = preview(d.items, d.source.id, targetId);
        if (items !== d.items) set({ ...d, items });
      }}
      onDragEnd={(event) => {
        const d = latest.current;
        set(undefined);
        if (d === undefined || event.canceled) return;
        const to = sectionOf(d.items, d.source.id);
        if (to === undefined) return;
        const index = d.items[to]!.indexOf(d.source.id);
        if (to === d.source.section && index === itemsOf(sections)[to]?.indexOf(d.source.id)) return;
        sidebar.vm.drop({ rowId: d.source.id, toSection: to, toIndex: index });
      }}
    >
      <DragContext.Provider value={value}>{children}</DragContext.Provider>
    </DragDropProvider>
  );
}

/**
 * Drag wrappers are not their own Tab stop or "draggable" button: dnd-kit would make each a focusable role=button with
 * aria-pressed, so every row was two Tab stops. The row inside is the stop; moves without a pointer are in its menu
 * ("Move to folder…", star). dnd-kit leaves attributes that are already present alone.
 */
const NOT_A_TAB_STOP = { tabIndex: -1, role: "none", "aria-roledescription": "", "aria-describedby": "" } as const;

/** A docs-tree row that can be dragged into a folder. */
export function DocsDraggable({ pageId, children }: { pageId: string; children: ReactNode }) {
  const { ref, isDragging } = useDraggable({ id: `p:${pageId}`, type: "page" });
  return (
    <div ref={ref} className={isDragging ? "sidebar-drag-source" : undefined} {...NOT_A_TAB_STOP}>
      {children}
    </div>
  );
}

function SortableRow({ id, index, section, type, children }: { id: string; index: number; section: string; type: string; children: ReactNode }) {
  const { ref, isDragging } = useSortable({ id, index, group: section, type, accept: accepts(section), disabled: type === "agent" });
  return (
    <div ref={ref} className={isDragging ? "sidebar-row-dragging" : undefined} data-sidebar-row={id} {...NOT_A_TAB_STOP}>
      {children}
    </div>
  );
}

function SectionDrop({ id, children }: { id: string; children: ReactNode }) {
  const { ref, isDropTarget } = useDroppable({ id, type: "section", accept: accepts(id), collisionPriority: 1 });
  return (
    <div ref={ref} className={isDropTarget ? "sidebar-drop sidebar-drop-over" : "sidebar-drop"}>
      {children}
    </div>
  );
}

type Props = {
  activeUrl: string;
  open: (url: string, target?: OpenTarget) => void;
  canSplit: boolean;
  canSponsor: boolean;
  onCreateAgent: () => void;
  onCreateChannel: () => void;
  onBrowseChannels: () => void;
};

/**
 * Opens a sheet from a menu item once the menu has closed: a context menu hands focus back to its row as it closes,
 * which would otherwise dismiss a dialog opened in the same click.
 */
export function afterMenu(fn: () => void): () => void {
  return () => {
    setTimeout(fn, 60);
  };
}

function rowUrl(slug: string, row: SidebarEntry): string {
  if (row.type === "page") return row.pageKind === "database" ? Shell.formatUrl({ type: "db", ws: slug, dbId: row.pageId }) : Shell.formatUrl({ type: "page", ws: slug, pageId: row.pageId });
  if (row.type === "agent") return row.channelId === undefined ? Shell.formatUrl({ type: "dm", ws: slug, userId: row.userId }) : Shell.formatUrl({ type: "channel", ws: slug, channelId: row.channelId });
  return Shell.formatUrl({ type: "channel", ws: slug, channelId: row.channelId });
}

export function SidebarSections(p: Props) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const sidebar = useSidebar();
  const { openNewMessage } = useNewMessageSheet();
  // No "New message" while Chat is off, even for owners and admins who may still look.
  const canMessage = useFeatureAccess()("chat") === "full";
  const { drag } = useContext(DragContext);
  const s = sidebar.state;
  const active = Shell.parseUrl(p.activeUrl);
  const isActive = (url: string) => Shell.normalizeUrl(url) === Shell.normalizeUrl(p.activeUrl);
  const folderCount = s.sections.filter((x) => x.folderId !== undefined).length;

  // While dragging a conversation with Starred empty, show it as a drop target.
  const draggingConversation = drag !== undefined && (drag.source.type === "channel" || drag.source.type === "dm");
  const shown: SidebarSection[] =
    draggingConversation && !s.sections.some((x) => x.id === "starred") ? [{ id: "starred", title: { key: "nav.starred", params: {} }, rows: [] }, ...s.sections] : s.sections;
  const byId: Record<string, SidebarEntry> = {};
  for (const section of shown) for (const r of section.rows) byId[r.id] = r;

  const rowMenu = (row: SidebarEntry): MenuItem[] => {
    const items: MenuItem[] = [];
    if (row.type === "agent") return items;
    if (row.type === "page") {
      items.push({ id: "move", label: t("sidebar.row.move"), icon: "folder", onSelect: afterMenu(() => sidebar.vm.openMove(row.id)) });
      return items;
    }
    const a = sidebar.vm.rowActions(row.id);
    if (a === undefined) return items;
    items.push(
      a.starred
        ? { id: "unstar", label: t("sidebar.row.unstar"), icon: "starOff", onSelect: () => sidebar.vm.setStarred(a.channelId, false) }
        : { id: "star", label: t("sidebar.row.star"), icon: "star", onSelect: () => sidebar.vm.setStarred(a.channelId, true) },
    );
    items.push({ id: "move", label: t("sidebar.row.move"), icon: "folder", onSelect: afterMenu(() => sidebar.vm.openMove(row.id)) });
    items.push(
      a.muted
        ? { id: "unmute", label: t("sidebar.row.unmute"), icon: "bell", onSelect: () => sidebar.vm.setMuted(a.channelId, false) }
        : { id: "mute", label: t("sidebar.row.mute"), icon: "bellOff", onSelect: () => sidebar.vm.setMuted(a.channelId, true) },
    );
    if (a.canLeave) items.push({ id: "leave", label: t("sidebar.row.leave"), icon: "leave", destructive: true, onSelect: () => sidebar.vm.leave(a.channelId) });
    if (a.canClose) items.push({ id: "close", label: t("dm.close"), icon: "close", onSelect: () => sidebar.vm.close(a.channelId) });
    return items;
  };

  const channelsAdd: MenuItem[] = [];
  if (s.canCreateChannels) channelsAdd.push({ id: "create-channel", label: t("channel.create"), icon: "plus", onSelect: afterMenu(p.onCreateChannel) });
  channelsAdd.push({ id: "browse-channels", label: t("channels.browse.title"), icon: "search", onSelect: afterMenu(p.onBrowseChannels) });
  channelsAdd.push({ id: "new-folder", label: t("sidebar.folder.new"), icon: "folder", onSelect: afterMenu(() => sidebar.vm.startCreateFolder()) });

  const folderMenu = (folderId: string, index: number): MenuItem[] => {
    const items: MenuItem[] = [{ id: "edit-folder", label: t("sidebar.folder.edit"), icon: "edit", onSelect: afterMenu(() => sidebar.vm.startEditFolder(folderId)) }];
    if (index > 0) items.push({ id: "folder-up", label: t("sidebar.folder.moveUp"), icon: "chevronUp", onSelect: () => sidebar.vm.moveFolder(folderId, -1) });
    if (index < folderCount - 1) items.push({ id: "folder-down", label: t("sidebar.folder.moveDown"), icon: "chevronDown", onSelect: () => sidebar.vm.moveFolder(folderId, 1) });
    items.push({ id: "delete-folder", label: t("sidebar.folder.delete"), icon: "trash", destructive: true, onSelect: () => sidebar.vm.deleteFolder(folderId) });
    return items;
  };

  const folderIds = shown.map((x) => x.folderId).filter((id): id is string => id !== undefined);
  return (
    <>
      {shown.map((section) => {
        const collapsed = s.collapsed.includes(section.id);
        const isFolder = section.folderId !== undefined;
        const ids = drag?.items[section.id] ?? section.rows.map((r) => r.id);
        const all = ids.map((id) => byId[id] ?? (id.startsWith("p:") ? ({ type: "page", id, pageId: id.slice(2), name: "", pageKind: "page" } as SidebarEntry) : undefined)).filter((r): r is SidebarEntry => r !== undefined);
        // Slack: a collapsed section still lists its unread conversations and the one on screen.
        const rows = collapsed && drag === undefined ? all.filter((r) => SidebarLogic.showsWhenCollapsed(r) || isActive(rowUrl(ws.slug, r))) : all;
        return (
          <div className="sidebar-group" key={section.id} data-testid={`sidebar-section-${section.id}`}>
            <SidebarSectionHeader
              title={tr(section.title)}
              icon={SECTION_ICONS[section.id] ?? ((section.icon ?? "folder") as IconName)}
              emoji={section.emoji}
              collapsed={collapsed}
              onToggle={() => sidebar.vm.toggleSection(section.id)}
              onAdd={section.id === "agents" && p.canSponsor ? p.onCreateAgent : section.id === "dms" && canMessage ? () => openNewMessage() : undefined}
              addMenu={section.id === "channels" ? channelsAdd : undefined}
              addLabel={section.id === "agents" ? t("agent.create.title") : section.id === "channels" ? t("sidebar.channels.add") : section.id === "dms" ? t("newMessage.title") : undefined}
              menu={section.folderId === undefined ? undefined : folderMenu(section.folderId, folderIds.indexOf(section.folderId))}
              menuLabel={t("sidebar.folder.options")}
              testID={`section-${section.id}`}
            />
            <SectionDrop id={section.id}>
              {collapsed && drag === undefined && rows.length === 0
                ? null
                : rows.length === 0 && isFolder
                  ? <EmptyFolderHint testID={`folder-empty-${section.folderId}`} />
                  : rows.map((row, index) => {
                      const url = rowUrl(ws.slug, row);
                      const props = {
                        selected: isActive(url) || (row.type === "agent" && active?.type === "dm" && active.userId === row.userId),
                        onPress: () => p.open(url),
                        onOpenInNewTab: () => p.open(url, "newTab"),
                        onOpenInSplit: p.canSplit ? () => p.open(url, "split") : undefined,
                        menu: rowMenu(row),
                      };
                      return (
                        <SortableRow key={row.id} id={row.id} index={index} section={section.id} type={row.type}>
                          <ModClick url={url} open={p.open}>
                            {row.type === "channel" ? (
                              <SidebarChannelRow row={row} {...props} />
                            ) : row.type === "dm" ? (
                              <SidebarDmRow row={row} {...props} />
                            ) : row.type === "page" ? (
                              <SidebarFolderPageRow row={row} {...props} />
                            ) : (
                              <SidebarAgentRow row={row} {...props} />
                            )}
                          </ModClick>
                        </SortableRow>
                      );
                    })}
              {section.more !== undefined && !(collapsed && drag === undefined) ? (
                // Slack: quieter conversations are a click away (Browse DMs in the New message sheet).
                <SidebarNavRow icon="more" label={t("sidebar.moreConversations", { count: section.more })} selected={false} onPress={() => openNewMessage()} testID="sidebar-dms-more" />
              ) : null}
            </SectionDrop>
          </div>
        );
      })}
    </>
  );
}
